import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { User } from "types";

export type EmploymentHistoryPayload = {
  startDate?: string;
  endDate?: string;
  position?: string;
  salaryAmount?: number;
  salaryType?: "annual" | "daily" | "hourly" | "biweekly";
  currency?: string;
};

export type EmploymentHistoryResponse = {
  success: boolean;
  message: string;
  data: User;
};

const updateEmploymentHistory = async (
  userId: string,
  entryId: string,
  payload: EmploymentHistoryPayload
): Promise<EmploymentHistoryResponse> => {
  const { data } = await customAxios.put<EmploymentHistoryResponse>(
    `/users/${userId}/employment-history/${entryId}`,
    payload
  );
  return data;
};

export const useUpdateEmploymentHistory = () => {
  const queryClient = useQueryClient();

  return useMutation<
    EmploymentHistoryResponse,
    AxiosError,
    { userId: string; entryId: string; payload: EmploymentHistoryPayload }
  >(
    ({ userId, entryId, payload }) =>
      updateEmploymentHistory(userId, entryId, payload),
    {
      onSuccess: async (data, variables) => {
        showNotification({
          title: "Success",
          message: data.message ?? "Employment history updated successfully",
          color: "green",
        });

        await queryClient.invalidateQueries(["users"]);
        await queryClient.invalidateQueries(["user", variables.userId]);
        await queryClient.invalidateQueries([
          "employmentHistory",
          variables.userId,
        ]);
      },
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to update employment history",
          color: "red",
        }),
    }
  );
};
